import React, { useRef, useState } from "react";
import { logError } from "../lib/log";
import { streamFetch } from "../lib/api";
import { readSse } from "../lib/sse";
import { Send, Sparkles, Swords, MessageSquare } from "lucide-react";
import { ModelPicker, useTutorModel } from "../components/ModelPicker";
import { FaceOff } from "../components/FaceOff";

const SUGGESTIONS = [
  "Explain the difference between ionic and covalent bonds with an example.",
  "How do I calculate the molar mass of Ca(OH)₂?",
  "Why is water polar and what does that cause?",
  "What is a limiting reagent — give me a simple example.",
];

const msg = (role, content) => ({ id: crypto.randomUUID(), role, content });

export default function Tutor() {
  const [messages, setMessages] = useState([
    msg("assistant", "Hi! I'm ChemBuddy. Ask me anything about high school chemistry — atoms, bonds, reactions, or that homework problem you're stuck on."),
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionIdRef = useRef(crypto.randomUUID());
  const { models, model, setModel, current } = useTutorModel();
  const [compare, setCompare] = useState(false);

  const setLast = (content) => setMessages((m) => [...m.slice(0, -1), { ...m[m.length - 1], content }]);

  const send = async (text0) => {
    const text = (text0 ?? input).trim();
    if (!text || busy) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, msg("user", text), msg("assistant", "")]);

    try {
      const resp = await streamFetch("/tutor/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionIdRef.current, model }),
      });
      if (!resp.ok || !resp.body) throw new Error(`Tutor request failed (${resp.status})`);
      let acc = "";
      await readSse(resp, (chunk) => { acc += chunk; setLast(acc); }, { json: false });
    } catch (e) {
      logError("Tutor stream failed", e);
      setLast("Sorry — I couldn't respond right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tutor-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300">
          <Sparkles size={12} /> AI Tutor · <span data-testid="tutor-model-label">{current?.label}</span>
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">ChemBuddy</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Ask, follow up, get worked examples. Explanations tailored for high school level.
        </p>
        <div className="mt-4 flex flex-wrap items-start gap-3">
          <div className="inline-flex rounded-md border border-white/10 bg-black/40 p-1 text-xs" role="tablist" data-testid="tutor-mode">
            <button role="tab" aria-selected={!compare} onClick={() => setCompare(false)} data-testid="tutor-mode-chat"
              className={`inline-flex items-center gap-1 rounded px-3 py-1.5 ${!compare ? "bg-cyan-500 text-black font-semibold" : "text-slate-300 hover:text-white"}`}>
              <MessageSquare size={12} /> Chat
            </button>
            <button role="tab" aria-selected={compare} onClick={() => setCompare(true)} data-testid="tutor-mode-compare"
              className={`inline-flex items-center gap-1 rounded px-3 py-1.5 ${compare ? "bg-amber-400 text-black font-semibold" : "text-slate-300 hover:text-white"}`}>
              <Swords size={12} /> Compare
            </button>
          </div>
          {!compare && (
            <div className="min-w-0 flex-1">
              <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-500">Choose your AI model — Claude or ChatGPT</div>
              <ModelPicker models={models} model={model} onChange={setModel} disabled={busy} />
            </div>
          )}
        </div>
      </div>

      {compare ? <FaceOff /> : (
      <div className="rounded-md border border-white/10 bg-black/40">
        <div className="max-h-[50dvh] min-h-[260px] space-y-4 overflow-y-auto p-4 sm:max-h-[60vh] sm:min-h-[320px] sm:p-6" data-testid="tutor-messages">
          {messages.map((m, i) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`msg-${m.role}-${i}`}
                className={`max-w-[85%] whitespace-pre-wrap rounded-md px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "border border-cyan-400/30 bg-cyan-500/10 text-cyan-100"
                    : "border border-white/10 bg-white/5 text-slate-100"
                }`}
              >
                {m.role === "assistant" && !m.content && (
                  <span className="font-mono-num text-slate-500">▍ thinking…</span>
                )}
                {m.content}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 p-3 sm:p-4">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={busy}
              data-testid="tutor-input"
              placeholder="Ask about atoms, bonds, moles, pH…"
              className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/60 px-3 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 sm:px-4"
            />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="tutor-send-btn"
              className="inline-flex shrink-0 items-center gap-2 rounded-md bg-cyan-500 px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500"
            >
              <Send size={14} /> <span className="hidden sm:inline">Send</span>
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="tutor-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={busy}
                data-testid={`tutor-suggestion-${i}`}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-left text-xs text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
